import { logger } from '@/core/logger';
import { getSdkClient, mapAppLocaleToContentful } from '@/core/services/contentfulClient';

export interface VentureListItem {
    id: string;
    name: string;
    logoUrl: string | null;
    heroUrl: string | null;
}

export interface VentureFeaturedItem {
    id: string;
    name: string;
    imageUrl: string | null;
    link: string | null;
}

export interface VentureDetail extends VentureListItem {
    description: string;
    websiteUrl: string | null;
    contactEmail: string | null;
    featuredItemsHeading: string | null;
    featuredItems: VentureFeaturedItem[];
}

const appendImageParams = (url: string | null, params: string): string | null => {
    if (!url) return null;
    return url.includes('?') ? `${url}&${params}` : `${url}?${params}`;
};

const extractAssetUrl = (asset: any): string | null => {
    const file = asset?.fields?.file;
    if (!file) return null;
    const localizedFile = typeof file === 'object' ? file : undefined;
    const resolved = localizedFile?.['en-US'] || localizedFile;
    const url = resolved?.url;
    return typeof url === 'string' ? `https:${url}` : null;
};

const getLocalizedString = (value: any, locale: string): string => {
    // Supports:
    // 1) Plain string fields (non-localized): "https://..."
    // 2) Localized fields: { 'en-US': '...', en: '...', ar: '...' }
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
        // Exact locale match first (e.g. 'en-US' or 'ar')
        const direct = (value as Record<string, unknown>)[locale];
        if (typeof direct === 'string') return direct;

        // Short locale (e.g. 'en' from 'en-US')
        const [short] = locale.split('-');
        if (short) {
            const shortMatch = (value as Record<string, unknown>)[short];
            if (typeof shortMatch === 'string') return shortMatch;
        }

        // Common fallbacks
        const localized = value as Record<string, unknown>;
        if (typeof localized['en-US'] === 'string') return localized['en-US'] as string;
        if (typeof localized['en'] === 'string') return localized['en'] as string;
        if (typeof localized['ar'] === 'string') return localized['ar'] as string;

        // Any first string value in object
        for (const k of Object.keys(localized)) {
            if (typeof localized[k] === 'string') return localized[k] as string;
        }
    }
    return '';
};

const ensureArray = (value: any): any[] => {
    return Array.isArray(value) ? value : [];
};

/**
 * Resolve a possibly-linked asset from the entry fields or the global includes map.
 * - If the field is already an expanded Asset (has fields.file), return it as-is
 * - If it is a Link (sys.linkType === 'Asset'), look it up in the includes map
 */
const resolveAsset = (value: any, assetMap: Map<string, any>): any | null => {
  if (!value) return null;
  // Already-expanded asset
  if (value?.fields?.file) return value;

  const id = value?.sys?.id;
  if (id && assetMap.has(id)) {
    return assetMap.get(id);
  }
  return null;
};

const mapFeaturedItems = (items: any[], locale: string): VentureFeaturedItem[] => {
    return ensureArray(items).map((entry) => {
        const fields = entry?.fields || {};
        const name = getLocalizedString(fields.name, locale);
        const asset = fields.image;
        const imageUrlRaw = extractAssetUrl(asset?.[locale] || asset?.['en-US'] || asset);
        // Featured items are small cards; use lower width/quality for performance.
        const imageUrl = appendImageParams(imageUrlRaw, 'fm=webp&q=70&w=480');
        const linkStr = getLocalizedString(fields.link, locale);
        const link = linkStr && typeof linkStr === 'string' ? linkStr : null;
        return {
            id: entry?.sys?.id || '',
            name,
            imageUrl,
            link,
        };
    }).filter((item) => item.id);
};

export async function fetchVentures(locale: 'en' | 'ar'): Promise<VentureListItem[]> {
    const client = getSdkClient();
    const cfLocale = mapAppLocaleToContentful(locale);

    if (!client) {
        logger.warn('Ventures', 'Contentful client unavailable');
        return [];
    }

    try {
        const res = await client.getEntries({
            content_type: 'ventures',
            locale: cfLocale,
            include: 1,
            order: ['fields.ventureName'],
            limit: 100,
        });

        logger.info('Ventures', 'Fetched ventures', {
            locale: cfLocale,
            total: res.total,
            count: res.items?.length,
        });

        // Build a map of included assets so we can resolve Link fields like homeGridImage
        const assetMap = new Map<string, any>();
        (res.includes?.Asset || []).forEach((asset: any) => {
            const id = asset?.sys?.id;
            if (id) {
                assetMap.set(id, asset);
            }
        });

    const mappedVentures = (res.items || []).map((item: any) => {
      const fields = item.fields || {};
      const name = getLocalizedString(fields.ventureName, cfLocale);
    
            const homeGridAsset = resolveAsset(fields.homeGridImage, assetMap);
            const homeGridImageRaw = extractAssetUrl(homeGridAsset);
            const logoUrl = appendImageParams(homeGridImageRaw, 'fm=webp&q=80&w=360');

      return {
        id: item.sys.id,
        name,
        logoUrl,
        heroUrl: null, // Not needed for list view
      };
    });

    // Custom ordering: Properties, Education, Hospitality, Landscape, BFM
    // Handle edge cases: missing items, case variations, unknown ventures, RTL (Arabic) names
    const orderMap: Record<string, number> = {
        // English keywords
        'properties': 1,
        'education': 2,
        'hospitality': 3,
        'landscape': 4,
        'bfm': 5,
        'facilities management': 5, // Alternative name for BFM
        // Arabic keywords (from Contentful: بلووم العقارية, بلووم للتعليم, بلووم للضيافة, بلووم لاندسكيب, بلووم لإدارة المرافق)
        'العقارية': 1, // Properties
        'للتعليم': 2, // Education
        'للضيافة': 3, // Hospitality
        'لاندسكيب': 4, // Landscape
        'لإدارة المرافق': 5, // Facilities Management
        'بي إف إم': 5, // BFM in Arabic
        'إدارة المرافق': 5, // Alternative for BFM
    };

    const getVentureOrder = (name: string): number => {
        if (!name || typeof name !== 'string') {
            return 999; // Handle null/undefined/empty names
        }
        
        const normalized = name.trim();
        const normalizedLower = normalized.toLowerCase();
        
        // Check for exact matches or partial matches
        // For Arabic keywords, compare directly (toLowerCase doesn't affect Arabic)
        // For English keywords, use lowercase comparison
        for (const [key, order] of Object.entries(orderMap)) {
            // Check if key contains Arabic characters (Arabic Unicode range)
            const hasArabic = /[\u0600-\u06FF]/.test(key);
            if (hasArabic) {
                // For Arabic, compare directly (case-insensitive doesn't apply)
                if (normalized.includes(key)) {
                    return order;
                }
            } else {
                // For English, use lowercase comparison
                if (normalizedLower.includes(key.toLowerCase())) {
                    return order;
                }
            }
        }
        
        // If not found, assign a high order number so unknown ventures appear last
        return 999;
    };

    // Sort by custom order, then alphabetically for items with same order
    const sortedVentures = mappedVentures.sort((a: VentureListItem, b: VentureListItem) => {
        const orderA = getVentureOrder(a.name);
        const orderB = getVentureOrder(b.name);
        
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        
        // If same order, sort alphabetically
        return a.name.localeCompare(b.name);
    });

    return sortedVentures;
  } catch (error: any) {
    logger.error('Ventures', 'Failed to fetch ventures', {
      locale: cfLocale,
      error: error?.message || error,
    });
    return [];
  }
}

export async function fetchVentureById(id: string, locale: 'en' | 'ar'): Promise<VentureDetail | null> {
    const client = getSdkClient();
    const cfLocale = mapAppLocaleToContentful(locale);

    if (!client) {
        logger.warn('Ventures', 'Contentful client unavailable');
        return null;
    }

    try {
        const entry = await client.getEntry(id, { locale: cfLocale, include: 2 });
        const fields = entry.fields || {};

        const name = getLocalizedString(fields.ventureName, cfLocale);
        const description = getLocalizedString(fields.description, cfLocale);
        const logoUrlRaw = extractAssetUrl(fields.ventureLogo);
        const heroUrlRaw = extractAssetUrl(fields.heroImage);
        const logoUrl = appendImageParams(logoUrlRaw, 'fm=webp&q=80&w=600');
        const heroUrl = appendImageParams(heroUrlRaw, 'fm=webp&q=85&w=1200');
        const featuredHeading = getLocalizedString(fields.featuredItemsHeading, cfLocale);
        const featuredRefs = ensureArray(fields.featuredItems);
        const featuredItems = mapFeaturedItems(featuredRefs, cfLocale);

        return {
            id,
            name,
            logoUrl,
            heroUrl,
            description,
          websiteUrl: (() => {
              const url = getLocalizedString(fields.websiteUrl, cfLocale);
              return url || null;
          })(),
          contactEmail: (() => {
              const email = getLocalizedString(fields.contantEmail, cfLocale);
              return email || null;
          })(),
            featuredItemsHeading: featuredHeading || null,
            featuredItems,
        };
    } catch (error: any) {
        logger.error('Ventures', 'Failed to fetch venture detail', { id, locale: cfLocale, error: error?.message || error });
        return null;
    }
}

